/**
 * Settings hash utility for scenario comparison.
 *
 * Computes a SHA-256 digest of all user settings to determine
 * whether the current configuration matches any saved scenario.
 * Used to enable/disable the "Save Scenario" button and to
 * display the active scenario name when settings match.
 */

/**
 * Compute a SHA-256 hex digest from a settings object.
 *
 * Determinism is guaranteed by JSON.stringify with sorted keys.
 * Uses the Web Crypto API (SubtleCrypto), available in all
 * modern browsers and Node 15+.
 *
 * @param settings - Plain object whose JSON representation will be hashed
 * @returns Hex-encoded SHA-256 digest
 */
function sortSettingsValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sortSettingsValue(item));
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(
      ([left], [right]) => left.localeCompare(right)
    );
    const result: Record<string, unknown> = {};
    for (const [key, nestedValue] of entries) {
      result[key] = sortSettingsValue(nestedValue);
    }
    return result;
  }

  return value;
}

export async function computeSettingsHash(
  settings: Record<string, unknown>
): Promise<string> {
  const json = JSON.stringify(sortSettingsValue(settings));
  const encoder = new TextEncoder();
  const data = encoder.encode(json);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
